/* eslint-disable max-lines */
import {
  ApiResponse,
  Comments,
  Company,
  DeleteComment,
  EditComment,
  InterviewDetails,
  Interviews,
  NewComment,
  Projects,
  Segmentation,
  Option,
  CreateProject,
  NameUniqueness,
  CreateCompany,
  GetUserPermissions,
} from '@customTypes';
import { EMPTY_STRING } from '@constants';

import { api } from './api';

export const homepageApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCompanies: builder.query<Option[], void>({
      query: () => ({
        url: '/company/user',
      }),
      transformResponse: (response: ApiResponse<Company[]>) =>
        response.payload?.map((el) => ({ value: el.id, label: el.name })),
      providesTags: ['Companies'],
    }),
    getUsername: builder.query<ApiResponse<string>, void>({
      query: () => ({
        url: '/user/name',
      }),
    }),
    getProjects: builder.query<Projects['Response'], Projects['Request']>({
      query: ({ companyId, creationMonth }) => ({
        url: '/project/user',
        params: {
          ...(companyId && { companyId }),
          ...(creationMonth && { creationMonth }),
        },
      }),
      providesTags: ['Projects'],
    }),
    getInterviews: builder.query<Interviews['Response'], Interviews['Request']>({
      query: (params) => ({
        url: '/interview/list',
        params,
      }),
      providesTags: ['InterviewList'],
    }),
    getInterviewData: builder.query<InterviewDetails['Response'], InterviewDetails['Request']>({
      query: () => ({
        url: 'details',
        isInterviewRequest: true,
      }),
      providesTags: ['InterviewDetails'],
    }),
    getComments: builder.query<Comments['Response'], void>({
      query: () => ({
        url: 'comment',
        isInterviewRequest: true,
      }),
      providesTags: ['Comments'],
    }),
    addComment: builder.mutation<NewComment['Response'], NewComment['Request']>({
      query: (body) => ({
        url: 'comment',
        method: 'POST',
        body,
        isInterviewRequest: true,
      }),
      invalidatesTags: ['Comments'],
    }),
    editComment: builder.mutation<EditComment['Response'], EditComment['Request']>({
      query: (body) => ({
        url: 'comment',
        method: 'PUT',
        body,
        isInterviewRequest: true,
      }),
      invalidatesTags: ['Comments'],
    }),
    deleteComment: builder.mutation<DeleteComment['Response'], DeleteComment['Request']>({
      query: (params) => ({
        url: 'comment',
        method: 'DELETE',
        params,
        isInterviewRequest: true,
      }),
      invalidatesTags: ['Comments'],
    }),
    saveSegmentation: builder.mutation<Segmentation['Response'], Segmentation['Request']>({
      query: ({ businessId, worksheetId, body }) => ({
        url: `interview/${businessId}/worksheet/${worksheetId}/segmentation`,
        method: 'POST',
        body,
      }),
    }),
    getGroupedCompanies: builder.query<ApiResponse<Record<string, Company[]>>, void>({
      query: () => ({
        url: '/company/new-project',
      }),
    }),
    createProject: builder.mutation<CreateProject['Response'], CreateProject['Request']>({
      query: ({ body, isDuplicateMode }) => ({
        url: `project${isDuplicateMode ? '/copy' : EMPTY_STRING}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Projects'],
    }),
    checkNameUniqueness: builder.mutation<NameUniqueness['Response'], NameUniqueness['Request']>({
      query: ({ companyId, entityType, name }) => ({
        url: `/company/${companyId}/is-name-unique`,
        method: 'GET',
        params: { entityType, name },
      }),
    }),
    createNewCompany: builder.mutation<CreateCompany['Response'], CreateCompany['Request']>({
      query: ({ body }) => ({
        url: '/company',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Companies'],
    }),
    checkCompanyUniqueness: builder.mutation<ApiResponse<boolean>, { name: string }>({
      query: ({ name }) => ({
        url: `/company/is-name-unique`,
        method: 'GET',
        params: { name },
      }),
    }),
    getUserPermissions: builder.query<GetUserPermissions['Response'], void>({
      query: () => ({
        url: '/user/roles',
      }),
    }),
  }),
});

export const {
  useGetCompaniesQuery,
  useGetInterviewsQuery,
  useGetProjectsQuery,
  useGetUsernameQuery,
  useGetInterviewDataQuery,
  useAddCommentMutation,
  useGetCommentsQuery,
  useSaveSegmentationMutation,
  useDeleteCommentMutation,
  useEditCommentMutation,
  useGetGroupedCompaniesQuery,
  useCreateProjectMutation,
  useCheckNameUniquenessMutation,
  useCreateNewCompanyMutation,
  useCheckCompanyUniquenessMutation,
  useGetUserPermissionsQuery,
} = homepageApi;
